import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function CategoriesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#FF8A00");
  const [toDelete, setToDelete] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openNew = () => { setEditing(null); setName(""); setColor("#FF8A00"); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setName(c.name); setColor(c.color || "#FF8A00"); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Nome obrigatório");
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, { name, color });
        toast.success("Categoria atualizada");
      } else {
        await api.post("/categories", { name, color });
        toast.success("Categoria criada");
      }
      setDialogOpen(false);
      fetch();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao salvar"));
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/categories/${toDelete.id}`);
      toast.success("Categoria removida");
      setToDelete(null);
      fetch();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao excluir"));
    }
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "#FF8A00" }}>Organização</div>
          <h2 className="font-display font-bold text-3xl mt-1 text-[#1F1F24]">Categorias</h2>
          <p className="text-gray-600 text-sm mt-1">{items.length} categoria(s) cadastrada(s).</p>
        </div>
        <Button className="vp-btn-primary text-white h-11 px-5 rounded-lg" onClick={openNew} data-testid="btn-new-category">
          <Plus className="w-4 h-4 mr-2" /> Nova categoria
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <div className="text-gray-500">Carregando...</div> : items.map((c) => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between hover:shadow-md transition-shadow" data-testid={`category-card-${c.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${c.color}20`, color: c.color }}>
                <Tag className="w-5 h-5" />
              </div>
              <div className="font-semibold text-[#1F1F24] truncate">{c.name}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(c)} className="p-1.5 text-gray-500 hover:text-[#FF8A00] hover:bg-orange-50 rounded-md"
                data-testid={`cat-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
              {isAdmin && (
                <button onClick={() => setToDelete(c)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md"
                  data-testid={`cat-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar categoria" : "Nova categoria"}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Organize as credenciais por categoria temática.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="cat-name">Nome</Label>
              <Input id="cat-name" data-testid="cat-input-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </div>
            <div>
              <Label htmlFor="cat-color">Cor</Label>
              <Input id="cat-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-11 w-24 p-1" data-testid="cat-input-color" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="vp-btn-primary text-white" data-testid="cat-form-submit">{editing ? "Atualizar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir <strong>{toDelete?.name}</strong>?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
